import { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import { Layout } from '../../components/Layout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { MagnifyingGlass, ArrowsClockwise, X, Eye, FileText } from '@phosphor-icons/react';

const INV_STATUS_LABELS = {
  not_processed: { label: 'Not Processed', color: 'bg-zinc-100 text-zinc-600' },
  pdf_uploaded: { label: 'PDF Uploaded', color: 'bg-amber-100 text-amber-700' },
  extracted: { label: 'Extracted', color: 'bg-purple-100 text-purple-700' },
  verified: { label: 'Verified', color: 'bg-cyan-100 text-cyan-700' },
  bom_generated: { label: 'BOM Generated', color: 'bg-emerald-100 text-emerald-700' },
  stock_calculated: { label: 'Stock Calculated', color: 'bg-blue-100 text-blue-700' },
  ordered: { label: 'Ordered', color: 'bg-orange-100 text-orange-700' },
  inward_done: { label: 'Inward Done', color: 'bg-teal-100 text-teal-700' },
  issued: { label: 'Issued', color: 'bg-indigo-100 text-indigo-700' },
  closed: { label: 'Closed', color: 'bg-zinc-200 text-zinc-700' },
};

function StatusBadge({ status }) {
  const info = INV_STATUS_LABELS[status] || INV_STATUS_LABELS.not_processed;
  return (
    <span data-testid={`inv-status-${status}`} className={`px-2 py-0.5 rounded text-xs font-bold ${info.color}`}>
      {info.label}
    </span>
  );
}

export function InventoryJOList() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [jos, setJos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');

  const fetchJOs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('jo_number', search);
      if (statusFilter !== 'all') params.append('inv_status', statusFilter);
      const { data } = await api.get(`/api/inventory/job-orders?${params}`);
      setJos(data);
    } catch (err) {
      if (err?.isAuthError) return;
      toast.error('Failed to load job orders');
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter]);

  useEffect(() => { fetchJOs(); }, [fetchJOs]);

  return (
    <Layout>
      <div className="space-y-4 pb-10">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-light tracking-tight text-zinc-950">Inventory - Job Orders</h1>
            <p className="text-sm text-zinc-500 mt-2">View existing Job Orders and manage inventory items</p>
          </div>
          <Button onClick={fetchJOs} variant="outline" className="h-10">
            <ArrowsClockwise size={16} weight="bold" className="mr-1.5" /> Refresh
          </Button>
        </div>

        {/* Filters */}
        <Card className="rounded-sm shadow-sm">
          <CardContent className="p-3 flex flex-wrap gap-3 items-end">
            <div className="flex-1 min-w-[200px]">
              <label className="text-xs font-bold text-zinc-500 uppercase tracking-wider">Search JO / Customer</label>
              <Input
                data-testid="inv-jo-search"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="JO Number or Customer Name"
                className="h-9 mt-1"
              />
            </div>
            <div className="min-w-[180px]">
              <label className="text-xs font-bold text-zinc-500 uppercase tracking-wider">Inventory Status</label>
              <select
                data-testid="inv-status-filter"
                value={statusFilter}
                onChange={e => setStatusFilter(e.target.value)}
                className="h-9 mt-1 text-sm border rounded px-2 w-full bg-white"
              >
                <option value="all">All Statuses</option>
                {Object.entries(INV_STATUS_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>{v.label}</option>
                ))}
              </select>
            </div>
            <Button data-testid="inv-search-btn" onClick={fetchJOs} className="h-9 bg-blue-600 hover:bg-blue-700 text-white">
              <MagnifyingGlass size={14} className="mr-1" /> Search
            </Button>
            {(search || statusFilter !== 'all') && (
              <Button onClick={() => { setSearch(''); setStatusFilter('all'); }} variant="outline" className="h-9 text-red-600 border-red-300">
                <X size={14} className="mr-1" /> Clear
              </Button>
            )}
          </CardContent>
        </Card>

        {/* JO Table */}
        <Card className="rounded-sm shadow-sm">
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center text-zinc-400">Loading...</div>
            ) : jos.length === 0 ? (
              <div className="p-8 text-center text-zinc-400">No job orders found</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-zinc-50 border-b">
                      <th className="text-left p-3 font-bold text-xs uppercase tracking-wider text-zinc-500">JO Number</th>
                      <th className="text-left p-3 font-bold text-xs uppercase tracking-wider text-zinc-500">Customer</th>
                      <th className="text-left p-3 font-bold text-xs uppercase tracking-wider text-zinc-500">Profile</th>
                      <th className="text-center p-3 font-bold text-xs uppercase tracking-wider text-zinc-500">Windows</th>
                      <th className="text-center p-3 font-bold text-xs uppercase tracking-wider text-zinc-500">SQFT</th>
                      <th className="text-left p-3 font-bold text-xs uppercase tracking-wider text-zinc-500">JO Status</th>
                      <th className="text-left p-3 font-bold text-xs uppercase tracking-wider text-zinc-500">Inv Status</th>
                      <th className="text-center p-3 font-bold text-xs uppercase tracking-wider text-zinc-500">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {jos.map(jo => (
                      <tr key={jo.jo_number} className="border-b hover:bg-zinc-50">
                        <td className="p-3 font-bold text-zinc-950">{jo.jo_number}</td>
                        <td className="p-3 text-zinc-700">{jo.customer_name}</td>
                        <td className="p-3 text-zinc-600 text-xs">{jo.profile_type}</td>
                        <td className="p-3 text-center">{jo.no_of_windows}</td>
                        <td className="p-3 text-center">{jo.total_sqft}</td>
                        <td className="p-3">
                          <span className="text-xs font-bold text-zinc-500">{jo.status?.replace(/_/g, ' ')}</span>
                        </td>
                        <td className="p-3"><StatusBadge status={jo.inventory_status} /></td>
                        <td className="p-3 text-center">
                          <Button
                            data-testid={`inv-view-${jo.jo_number}`}
                            size="sm"
                            onClick={() => navigate(`/inventory/jo/${jo.jo_number}`)}
                            className="h-7 px-3 bg-orange-600 hover:bg-orange-700 text-white text-xs"
                          >
                            <Eye size={12} className="mr-1" /> Manage
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
